import * as THREE from 'three';
import {
  CAM_BACK, CAM_HEIGHT, CAM_LOOK, CAM_FOLLOW_RATE, PLANET_R,
} from '../config.js';
import { surfaceRadius } from '../world/planet.js';
import { events } from '../core/events.js';

// Intro: for the first few seconds the camera sweeps in from space down to the
// normal gameplay position. Emits 'camera:intro:done' when finished so other
// systems (narrative, fog) can react.
const INTRO_DURATION = 3.0;

// Scroll-wheel zoom — multiplier applied to CAM_BACK and CAM_HEIGHT. A
// target value is set by wheel input, the live value eases toward it.
// deltaY magnitude is used (not just its sign) so a trackpad's many
// small events yield many small increments — feels smooth under both
// trackpad flicks and mouse notches.
const ZOOM_MIN  = 0.6;
const ZOOM_MAX  = 1.6;
const ZOOM_GAIN = 0.0012;      // zoom-target units per raw deltaY pixel
const ZOOM_EASE = 12;          // exponential approach rate per second
let zoomTarget  = 1.0;
let zoom        = 1.0;

addEventListener('wheel', (e) => {
  if (e.deltaY === 0) return;
  e.preventDefault();
  // Clamp extreme single-event jumps (some browsers fire deltaY ~1000 on
  // inertial trackpad scrolls) so one flick can't bullrush the full range.
  const capped = Math.max(-120, Math.min(120, e.deltaY));
  zoomTarget += capped * ZOOM_GAIN;
  if (zoomTarget < ZOOM_MIN) zoomTarget = ZOOM_MIN;
  if (zoomTarget > ZOOM_MAX) zoomTarget = ZOOM_MAX;
}, { passive: false });

const _target     = new THREE.Vector3();
const _lookAt     = new THREE.Vector3();
const _introStart = new THREE.Vector3();
const _introEnd   = new THREE.Vector3();

let introElapsed = 0;
let introActive  = true;
let introProgress = 0;     // eased 0..1 for external consumers (fog, etc.)
let cinematicActive = false;

// External systems (e.g. the ending cutscene) can take over camera
// control by flipping this flag; while true, updateCamera() short-
// circuits and the caller is expected to set camera.position/lookAt
// each frame themselves.
export function setCinematicCamera(active) {
  cinematicActive = active;
}

// Cubic ease-in-out — slow start, fast middle, slow settle.
function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

// Places the camera at its intro start — far above and behind the character,
// looking down at the planet. Also primes the gameplay target vectors.
export function initCamera(camera, charPos, charForward) {
  const groundR = surfaceRadius(charPos);

  // Start position: ~9× planet radius along the character's up axis, offset
  // behind by 3× planet radius so the approach angles in from over the
  // shoulder rather than straight down.
  _introStart.copy(charPos).multiplyScalar(PLANET_R * 9)
    .addScaledVector(charForward, -PLANET_R * 3);

  camera.position.copy(_introStart);
  camera.up.copy(charPos);
  _lookAt.copy(charPos).multiplyScalar(groundR + CAM_LOOK);
  camera.lookAt(_lookAt);
}

// Called each frame. During the intro, interpolates from the space start
// position to the normal gameplay follow position. After the intro, it's a
// standard exponential-approach third-person follow.
export function updateCamera(camera, charPos, charForward, altitude, dt) {
  if (cinematicActive) return;
  const groundR = surfaceRadius(charPos);

  // Ease the live zoom toward the wheel-driven target.
  zoom += (zoomTarget - zoom) * Math.min(1, dt * ZOOM_EASE);

  _target.copy(charPos).multiplyScalar(groundR + altitude + CAM_HEIGHT * zoom)
    .addScaledVector(charForward, -CAM_BACK * zoom);

  if (introActive) {
    introElapsed += dt;
    const t = Math.min(1, introElapsed / INTRO_DURATION);
    introProgress = easeInOutCubic(t);

    _introEnd.copy(_target);
    camera.position.lerpVectors(_introStart, _introEnd, introProgress);
    camera.up.copy(charPos);
    _lookAt.copy(charPos).multiplyScalar(groundR + altitude + CAM_LOOK);
    camera.lookAt(_lookAt);

    if (t >= 1) {
      introActive = false;
      introProgress = 1;
      events.emit('camera:intro:done');
    }
    return;
  }

  camera.position.lerp(_target, 1 - Math.exp(-dt * CAM_FOLLOW_RATE));
  camera.up.copy(charPos);
  _lookAt.copy(charPos).multiplyScalar(groundR + altitude + CAM_LOOK);
  camera.lookAt(_lookAt);
}

export function isIntroActive()   { return introActive; }
export function getIntroProgress() { return introProgress; }

// Snaps the camera instantly to the current gameplay target — used after
// teleporting the player so the camera doesn't lerp across the planet.
export function snapCamera(camera, charPos, charForward) {
  const groundR = surfaceRadius(charPos);
  _target.copy(charPos).multiplyScalar(groundR + CAM_HEIGHT * zoom)
    .addScaledVector(charForward, -CAM_BACK * zoom);
  camera.position.copy(_target);
  camera.up.copy(charPos);
  _lookAt.copy(charPos).multiplyScalar(groundR + CAM_LOOK);
  camera.lookAt(_lookAt);
}
